//src/pages/po/AdvancedSearchRequestsKanbanModalContent.tsx
import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@/icons';
import SearchButton from '../../components/common/SearchButton';
import DateRangeInput from '../../components/common/DateRangeInput';
import { Contato } from '../../types/contatos/Contato';
import { Requisicao } from '../../types/compras/Requisicao';
import './AdvancedSearchRequestsKanbanModalContent.css';
import { useApiCall } from '../../hooks/useApiCall';
import { showAlert } from '../../contexts/DialogContext';
import { Toast } from '../../contexts/ToastContext';
import { logger } from '@/utils/logger';

interface AdvancedSearchRequestsKanbanModalContentProps {
  theme: 'dark' | 'light';
  onClose: () => void;
  onSearch: (result: {
    aguardandoAbertura: { content: Contato[]; page: { totalPages: number; totalElements: number } };
    pendentes: { content: Requisicao[]; page: { totalPages: number; totalElements: number } };
    emProcesso: { content: Requisicao[]; page: { totalPages: number; totalElements: number } };
    finalizadas: { content: Requisicao[]; page: { totalPages: number; totalElements: number } };
  }, params: URLSearchParams) => void;
}

const AdvancedSearchRequestsKanbanModalContent: React.FC<AdvancedSearchRequestsKanbanModalContentProps> = ({
  theme,
  onClose,
  onSearch,
}) => {
  const [id, setId] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [codProduto, setCodProduto] = useState('');
  const [nomeProduto, setNomeProduto] = useState('');
  const [codCategoria, setCodCategoria] = useState('');
  const [nomeCategoria, setNomeCategoria] = useState('');
  const [prioridade, setPrioridade] = useState('');
  const [loja, setLoja] = useState('');
  const [requisitante, setRequisitante] = useState('');
  const [origem, setOrigem] = useState<'' | 'PLANEJAMENTO' | 'BALCAO'>('');
  const [consultor, setConsultor] = useState('');
  const [contato, setContato] = useState('');
  const [codCliente, setCodCliente] = useState('');
  const [nomeCliente, setNomeCliente] = useState('');
  const { callApi, isLoading } = useApiCall();

  const filterValuesRef = useRef({
    id: '',
    dataInicio: '',
    dataFim: '',
    codProduto: '',
    nomeProduto: '',
    codCategoria: '',
    nomeCategoria: '',
    prioridade: '',
    loja: '',
    requisitante: '',
    origem: '',
    contato: '',
    codCliente: '',
    nomeCliente: '',
    consultor: '',
  });

  useEffect(() => {
    filterValuesRef.current = {
      id,
      dataInicio,
      dataFim,
      codProduto,
      nomeProduto,
      codCategoria,
      nomeCategoria,
      prioridade,
      loja,
      requisitante,
      origem,
      contato,
      codCliente,
      nomeCliente,
      consultor,
    };
  }, [
    id,
    dataInicio,
    dataFim,
    codProduto,
    nomeProduto,
    codCategoria,
    nomeCategoria,
    prioridade,
    loja,
    requisitante,
    origem,
    contato,
    codCliente,
    nomeCliente,
    consultor,
  ]);

  const handleSearch = async () => {
    const {
      id,
      dataInicio,
      dataFim,
      codProduto,
      nomeProduto,
      codCategoria,
      nomeCategoria,
      prioridade,
      loja,
      requisitante,
      origem,
      contato,
      codCliente,
      nomeCliente,
      consultor,
    } = filterValuesRef.current;

    const hasFilter =
      id.trim() !== '' ||
      dataInicio.trim() !== '' ||
      dataFim.trim() !== '' ||
      codProduto.trim() !== '' ||
      nomeProduto.trim() !== '' ||
      codCategoria.trim() !== '' ||
      nomeCategoria.trim() !== '' ||
      prioridade.trim() !== '' ||
      loja.trim() !== '' ||
      requisitante.trim() !== '' ||
      origem.trim() !== '' ||
      contato.trim() !== '' ||
      codCliente.trim() !== '' ||
      nomeCliente.trim() !== '' ||
      consultor.trim() !== '';

    if (!hasFilter) {
      await showAlert('Pelo menos um parâmetro de filtro deve ser fornecido.', { variant: 'warning' });
      return;
    }

    try {
      const queryParams = new URLSearchParams();
      queryParams.append('page', '0');
      queryParams.append('size', '10');
      if (id) queryParams.append('id', id.trim());
      if (dataInicio) queryParams.append('dataInicio', dataInicio.trim());
      if (dataFim) queryParams.append('dataFim', dataFim.trim());
      if (codProduto) queryParams.append('codProduto', codProduto.trim());
      if (nomeProduto) queryParams.append('nomeProduto', nomeProduto.trim());
      if (codCategoria) queryParams.append('codCategoria', codCategoria.trim());
      if (nomeCategoria) queryParams.append('nomeCategoria', nomeCategoria.trim());
      if (prioridade) queryParams.append('prioridade', prioridade.trim());
      if (loja) queryParams.append('loja', loja.trim());
      if (requisitante) queryParams.append('requisitante', requisitante.trim());
      if (origem) queryParams.append('origem', origem);
      if (contato) queryParams.append('contato', contato.trim());
      if (codCliente) queryParams.append('codCliente', codCliente.trim());
      if (nomeCliente) queryParams.append('nomeCliente', nomeCliente.trim());
      if (consultor) queryParams.append('consultor', consultor.trim());

      const endpoint = `/v1/busca-inteligente/capa-contato-requisicao?${queryParams.toString()}`;
      await callApi('get', endpoint, null, {
            onSuccess: (data) => {
              onSearch({
                aguardandoAbertura: { content: data.aguardandoAbertura.content || [], page: data.aguardandoAbertura.page },
                pendentes: { content: data.pendentes.content || [], page: data.pendentes.page },
                emProcesso: { content: data.emProcesso.content || [], page: data.emProcesso.page },
                finalizadas: { content: data.finalizadas.content || [], page: data.finalizadas.page },
              }, queryParams);
              Toast.success('Pesquisa realizada com sucesso!');
              onClose();
            },
          });
        } catch (err) {
          logger.error('Erro ao buscar:', err);
          await showAlert('Erro ao realizar a busca.', { variant: 'error' });
        }
        // Removido finally: gerenciado pelo hook.
      };

  return (
    <div className={`advanced-search-requests-kanban-modal-content ${theme}`}>
      <h2><FontAwesomeIcon icon={faMagnifyingGlass} /> Pesquisa Avançada</h2>
      <div className="search-form">
        <div className="form-inputs">
          <div className="form-row">
            <input
              type="text"
              value={id}
              onChange={(e) => setId(e.target.value)}
              placeholder="Requisição"
              className="search-input"
            />
            <DateRangeInput
              dataInicio={dataInicio}
              setDataInicio={setDataInicio}
              dataFim={dataFim}
              setDataFim={setDataFim}
              theme={theme}
            />
            <input
              type="text"
              value={codProduto}
              onChange={(e) => setCodProduto(e.target.value)}
              placeholder="Código"
              className="search-input"
            />
            <input
              type="text"
              value={nomeProduto}
              onChange={(e) => setNomeProduto(e.target.value)}
              placeholder="Nome do Produto"
              className="search-input"
            />
          </div>
          <div className="form-row">
            <input
              type="text"
              value={codCategoria}
              onChange={(e) => setCodCategoria(e.target.value)}
              placeholder="Código Categoria"
              className="search-input"
            />
            <input
              type="text"
              value={nomeCategoria}
              onChange={(e) => setNomeCategoria(e.target.value)}
              placeholder="Categoria"
              className="search-input"
            />
            <input
              type="text"
              value={prioridade}
              onChange={(e) => setPrioridade(e.target.value)}
              placeholder="Prioridade"
              className="search-input"
            />
            <input
              type="text"
              value={loja}
              onChange={(e) => setLoja(e.target.value)}
              placeholder="Loja Solicitante"
              className="search-input"
            />
            <input
              type="text"
              value={requisitante}
              onChange={(e) => setRequisitante(e.target.value)}
              placeholder="Requisitante"
              className="search-input"
            />
          </div>
          <div className="form-row">
            <select
              value={origem}
              onChange={(e) => setOrigem(e.target.value as '' | 'PLANEJAMENTO' | 'BALCAO')}
              className="form-select"
            >
              <option value="">Requisitado por...</option>
              <option value="BALCAO">Balcão</option>
              <option value="PLANEJAMENTO">Planejamento</option>
            </select>
          </div>
          <div className="form-row">
            {/*TODO: Ajustar para envio ao backend*/}
            <input
              type="text"
              value={consultor}
              onChange={(e) => setConsultor(e.target.value)}
              placeholder="Consultor"
              className="search-input"
            />
            <input
              type="text"
              value={contato}
              onChange={(e) => setContato(e.target.value)}
              placeholder="Contato"
              className="search-input"
            />
            <input
              type="text"
              value={codCliente}
              onChange={(e) => setCodCliente(e.target.value)}
              placeholder="Código Cliente"
              className="search-input"
            />
            <input
              type="text"
              value={nomeCliente}
              onChange={(e) => setNomeCliente(e.target.value)}
              placeholder="Nome Cliente"
              className="search-input"
            />
          </div>
        </div>
        <SearchButton isLoading={isLoading} onClick={handleSearch} disabled={isLoading}>
          Procurar
        </SearchButton>
      </div>
    </div>
  );
};

export default AdvancedSearchRequestsKanbanModalContent;